import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Pet, { PetCustomPrice } from '@spike/pet-model';
import {
    BookingCustomPriceKind,
    findDropoffCustomPrice,
    findPickupCustomPrice,
    findStandardCustomPrice
} from '../utils/bookingCustomPrices';

export type SavedCustomPriceMode = 'custom' | 'standard';

interface Params {
    petId?: number;
    serviceId?: number;
    catalogPrice: number;
    currentPrice: number;
    useSavedCustomPrice?: boolean;
    kind?: BookingCustomPriceKind;
}

export function useSavedCustomPriceChoice(params: Params) {
    const pets = useSelector<RootState, Array<Pet>>(state => state.pets.list);
    const customPrices = useSelector<RootState, Array<PetCustomPrice>>(state => state.pets.customPrices);

    const petName = pets.find(pet => pet.id === params.petId)?.name || 'this pet';
    const kind = params.kind ?? 'standard';
    const saved =
        params.serviceId == null
            ? undefined
            : kind === 'pickup'
            ? findPickupCustomPrice(customPrices || [], params.serviceId)
            : kind === 'dropoff'
            ? findDropoffCustomPrice(customPrices || [], params.serviceId)
            : findStandardCustomPrice(customPrices || [], params.serviceId);

    const savedAmount =
        kind === 'pickup' ? saved?.pickupPrice : kind === 'dropoff' ? saved?.dropoffPrice : saved?.price;
    const savedPrice = Number(savedAmount);
    const show = savedAmount != null && Number.isFinite(savedPrice);

    const mode: SavedCustomPriceMode = (() => {
        if (!show) return 'standard';
        if (params.useSavedCustomPrice === false) return 'standard';
        if (params.useSavedCustomPrice === true) return 'custom';
        if (Math.abs(Number(params.currentPrice) - savedPrice) < 0.004) return 'custom';
        return 'standard';
    })();

    const applyMode = (next: SavedCustomPriceMode) => ({
        price: next === 'custom' ? savedPrice : Number(params.catalogPrice) || 0,
        useSavedCustomPrice: next === 'custom',
        customPriceId: saved?.id
    });

    return { show, petName, savedPrice, mode, applyMode };
}
